import { Localized, useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { Dialog } from "cerulean";
import type { TemplateActivePaymentsCounts } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import { useIsACHDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";

type EditActiveTemplateDialogProps = {
  isOpen: boolean;
  counts: TemplateActivePaymentsCounts;
  onClose: () => void;
  onKeepEditing: () => void;
};

const EditActiveTemplateDialog = ({
  isOpen,
  counts,
  onClose,
  onKeepEditing,
}: EditActiveTemplateDialogProps) => {
  const { l10n } = useLocalization();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();

  const hasScheduled = counts.scheduledBulkCount > 0;
  const hasPending = counts.pendingBulkCount > 0;

  return (
    <Dialog
      width="min(595px, 90vw)"
      isOpen={isOpen}
      onUserDismiss={onClose}
      title={l10n.getString("edit-active-template-dialog-title")}
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              onClick={onClose}
              testId="edit-active-template-cancel"
            >
              {l10n.getString("button-cancel")}
            </Button>
          </Row.Item>
          <Row.Item shrink>
            <Button
              onClick={onKeepEditing}
              testId="edit-active-template-confirm"
            >
              {l10n.getString("edit-active-template-dialog-keep-editing")}
            </Button>
          </Row.Item>
        </Row>
      }
    >
      <p className="margin--top--l margin--bottom--l">
        {l10n.getString("edit-active-template-dialog-content")}
      </p>
      <ul className="margin--bottom--l">
        {hasScheduled && (
          <Localized
            id="edit-active-template-dialog-scheduled"
            vars={{
              bulkCount: counts.scheduledBulkCount,
              individualCount: counts.scheduledIndividualCount,
            }}
            elems={{ bold: <strong /> }}
          >
            <li>
              <strong>
                {counts.scheduledBulkCount} scheduled bulk payments
              </strong>{" "}
              (containing {counts.scheduledIndividualCount} individual payments)
            </li>
          </Localized>
        )}
        {hasPending && (
          <Localized
            id="edit-active-template-dialog-pending"
            vars={{
              bulkCount: counts.pendingBulkCount,
              individualCount: counts.pendingIndividualCount,
            }}
            elems={{ bold: <strong /> }}
          >
            <li>
              <strong>{counts.pendingBulkCount} pending bulk payments</strong>{" "}
              (containing {counts.pendingIndividualCount} individual payments)
            </li>
          </Localized>
        )}
      </ul>
      {achDualApprovalRequired ? (
        <p>{l10n.getString("edit-active-template-dialog-footer")}</p>
      ) : null}
    </Dialog>
  );
};

export default EditActiveTemplateDialog;
